import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  Box,
  CustomSelect,
  Divider,
  ErrorInfo,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { useHistory, useParams } from 'react-router-dom';
import { UploadedFile } from 'web-library/models/UploadedFile';
import RouteKey from '../../routes/key';
import {
  useGetWithdrawalDraft,
  useGetWithdrawalDropDown,
  usePutExamWithdrawalDetails,
} from '../../api/candidate/exam/@candidate/queries-services/queries';
import { withdrawalTypeData } from '../admin/logic/dropDownOption';
import UploadDocuments from '../../pages/accessArrangement/SelectCondition/components/UploadDocuments';
import useSetUploadButtonStatus from '../../hooks/useSetUploadButtonStatus';
import WithdrawalReasonButtons from './WithdrawalReasonButtons';
import { attachedFilesService } from '../../api';
import WithdrawalReasonDocumentInfo from './WithdrawalReasonDocumentInfo';
import { WithdrawalReason as WithdrawalReasonModel } from '../../api/candidate/exam/model';
import WithdrawalReasonAdditionalText from './WithdrawalReasonAdditionalText';
import { ApplicationType } from '../../api/candidate/config/model';
import { prepareFiles } from '../../utils/UploadUtil';
import usePostPresignLinks from './logic/usePostPresignLinks';

const useStyles = makeStyles()((theme) =>
({
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subjectText: {
    width: '100%',
  },
  actionButton: {
    padding: 0,

    '& span': {
      justifyContent: 'flex-start',
    },
  },
  editContainer: {
    marginBottom: theme.spacing(6),
  },
  deleteButton: {
    color: '#E55934',
  },
  deleteContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  buttonArea: {
    marginTop: theme.spacing(4),
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  optionalPapers: {
    color: theme.color.text.textTertiary,
  },
  inputLabel: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  dropZone: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    width: '100%',
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

const { examRegistrations: withdrawalRoute, review } = RouteKey.withdrawal.child;
const { withdrawal, exam } = RouteKey.child;

const WithdrawalReasonHeader = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <SectionHeader title={t('withdrawal->>ReviewWithdrawal.ReasonForWithdrawal')} />

      <Typography className={classes.header} variant="h3">
        {t('withdrawal->>WithdrawalReason.SelectReasonForWithdrawal')}
      </Typography>
    </>
  );
};

const WithdrawalReason = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { uuid: applicationUUID } = useParams<any>();
  const history = useHistory();

  const [isFetchedDraft, setIsFetchedDraft] = useState(false);
  const [selectReason, setSelectReason] = useState<string>();
  const [selectedArr, setSelectedArr] = useState<WithdrawalReasonModel>();
  const [textArea, setTextArea] = useState<string>();
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>();
  const [errorHelperText, setErrorHelperText] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const { refetch: getWithdrawalDraft } = useGetWithdrawalDraft(
    (res) => {
      if (res.reason.reasonDescription) {
        setSelectReason(res.reason.reasonDescription);
      }
      if (res.specifiedReason) {
        setTextArea(res.specifiedReason);
      }
      if (res.fileEntityModelList) {
        const filesDraft = res.fileEntityModelList?.map((draftFile) => {
          const newFile = new File([new ArrayBuffer(draftFile.fileSize)], draftFile.fileName);
          return {
            file: newFile,
            hasError: false,
            pending: false,
            uploaded: true,
            key: draftFile.uuid,
          };
        });
        setDraftFiles(filesDraft);
      }
      setIsFetchedDraft(true);
    },
    () => {
      history.replace('/error/generic');
    },
    applicationUUID
  );

  const { data: dropdownData, isLoading: isLoadingDropdownData } = useGetWithdrawalDropDown(() =>
    getWithdrawalDraft()
  );

  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile(
    () => null
  );

  const onSuccess = useCallback(() => {
    history.push(`/${withdrawal}/${withdrawalRoute.route}/${applicationUUID}/${review.route}`);
  }, [history, applicationUUID]);

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: onSuccess,
  });

  useEffect(() => {
    if (dropdownData && selectReason) {
      setSelectedArr(dropdownData.find((value) => value.reasonDescription === selectReason));
    }
  }, [dropdownData, selectReason]);

  const usePostPresignLinksMutation = usePostPresignLinks({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
    setErrorMsg,
  });

  const putReviewDetails = usePutExamWithdrawalDetails(
    () => {
      if (files && files.length > 0) {
        const { filenames } = prepareFiles(files, setFiles);
        usePostPresignLinksMutation.mutate({
          payload: {
            applicationType: 'EXAM',
            uuid: applicationUUID,
            filenames,
          },
        });
      } else {
        onSuccess();
      }
    },
    (err) => {
      setErrorMsg(err?.response?.data.message);
      setApiButtonStatus('error');
    }
  );
  const handleSelectChange = (value: string) => {
    if (files) {
      files.forEach((file) => {
        if (file.key) {
          deleteFileMutate(file.key);
        }
      });
    }
    setSelectReason(value);
    setFiles([]);
    setTextArea(undefined);
  };
  const handleBack = () => history.push(`/${exam}/${withdrawalRoute.route}`);
  const handleConfirm = () => {
    const errors = [];
    if (!selectReason) {
      setErrorHelperText(t('withdrawal->>WithdrawalReason.NoReasonSelectedError'));
    } else {
      if (selectedArr?.reasonDescription !== 'Others' && !files.length && !draftFiles.length) {
        errors.push(t('withdrawal->>WithdrawalReason.FileUploadErrorMessage'));
      }
      if (selectedArr?.additionalTextNotice && !textArea) {
        errors.push(t('withdrawal->>WithdrawalReason.SpecifiedReasonErrorMessage'));
      }
    }
    if (errors.length) {
      setDisplayErrors(true);
      setFormErrors(errors);
    } else if (selectedArr) {
      setErrorMsg('');
      putReviewDetails.mutate({
        payload: {
          uuid: applicationUUID,
          reason: selectedArr,
          specifiedReason: textArea,
        },
      });
    }
  };
  return (
    <>
      {isLoadingDropdownData ? (
        <Progress color="secondary" />
      ) : (
        <>
          <WithdrawalReasonHeader />
          {dropdownData && isFetchedDraft && (
            <CustomSelect
              id="select-withdrawal-reason"
              label={t('withdrawal->>WithdrawalReason.SelectReasonForWithdrawal')}
              placeholder={t('withdrawal->>WithdrawalReason.SelectReason')}
              helperText={errorHelperText}
              error={!selectReason && !!errorHelperText}
              displayEmpty
              value={selectReason}
              onChange={async (e) => {
                handleSelectChange(e.target.value as string);
                setErrorHelperText('');
                setSelectedArr(
                  dropdownData.find(
                    (value) => value.reasonDescription === (e.target.value as string)
                  )
                );
              }}
              renderValue={() => (
                <Typography>
                  {selectReason || t('withdrawal->>WithdrawalReason.SelectReason')}
                </Typography>
              )}
              menuItems={withdrawalTypeData(dropdownData)}
            />
          )}
          {selectedArr && (
            <>
              <WithdrawalReasonAdditionalText
                selectedArr={selectedArr}
                textArea={textArea}
                setTextArea={setTextArea}
                displayErrors={displayErrors}
              />
              <Divider paddingTop />
              <WithdrawalReasonDocumentInfo selectedArr={selectedArr} />
              <UploadDocuments
                files={files}
                setFiles={setFiles}
                applicationType={ApplicationType.EXAM}
                draftFiles={draftFiles}
                setDraftFiles={setDraftFiles}
              />
            </>
          )}
          <ErrorInfo label={errorMsg} />
          <Box className={classes.buttonContainer}>
            <WithdrawalReasonButtons
              files={files}
              apiButtonStatus={apiButtonStatus}
              textArea={textArea}
              selectReason={selectReason}
              displayErrors={displayErrors}
              setDisplayErrors={setDisplayErrors}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              handleBack={handleBack}
              handleConfirm={handleConfirm}
            />
          </Box>
        </>
      )}
    </>
  );
};
export default WithdrawalReason;
